import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BASE_PATH } from '@app/api/user-service';
import { RoleManagementService } from '@app/api/user-service/api/roleManagement.service';
import { KeycloakRoleResponse } from '@app/api/user-service/model/keycloakRoleResponse';
import { PageInfoKeycloakRoleResponse } from '@app/api/user-service/model/pageInfoKeycloakRoleResponse';
import {
  JobPosition,
  JobRoleMapping,
  JobRoleMappingRequest,
  PageInfo,
} from '../models/job-role-mapping.model';

/**
 * Technical Keycloak realm roles that should never be offered as a mapping target.
 * These are Keycloak internals, not business roles.
 */
const TECHNICAL_ROLE_PREFIXES = ['default-roles-'];
const TECHNICAL_ROLE_NAMES = new Set(['offline_access', 'uma_authorization']);

/**
 * Data access for the Odoo Job Position -> Keycloak Role mapping feature.
 *
 * The /v1/job-positions and /v1/job-role-mappings endpoints are not part of the
 * generated user-service client, so this service calls them directly using the
 * same BASE_PATH the generated client resolves (the user-service gateway prefix).
 * The auth interceptor attaches the bearer token automatically.
 */
@Injectable({ providedIn: 'root' })
export class JobRoleMappingService {
  private readonly http = inject(HttpClient);
  private readonly apiRoleManagementService = inject(RoleManagementService);
  private readonly basePath = inject(BASE_PATH, { optional: true }) ?? '';

  private get jobPositionsUrl(): string {
    return `${this.basePath}/v1/job-positions`;
  }

  private get mappingsUrl(): string {
    return `${this.basePath}/v1/job-role-mappings`;
  }

  /** Browse Odoo job positions (the "roles from Odoo" the admin picks from). */
  searchJobPositions(
    page = 0,
    size = 20,
    search?: string,
  ): Observable<PageInfo<JobPosition>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    const term = search?.trim();
    if (term) {
      params = params.set('search', term);
    }
    return this.http
      .get<PageInfo<JobPosition>>(this.jobPositionsUrl, { params })
      .pipe(
        catchError((error) => {
          console.error('Error searching job positions:', error);
          return throwError(() => error);
        }),
      );
  }

  /** List existing job-role mappings (one item per mapped job position). */
  listMappings(
    page = 0,
    size = 1000,
    odooJobId?: number,
  ): Observable<PageInfo<JobRoleMapping>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    if (odooJobId != null) {
      params = params.set('odooJobId', String(odooJobId));
    }
    return this.http
      .get<PageInfo<JobRoleMapping>>(this.mappingsUrl, { params })
      .pipe(
        catchError((error) => {
          console.error('Error listing job-role mappings:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Persist the desired role set for a job position.
   *
   * The backend PUT requires a non-empty `roleNames` (400 otherwise), so an empty
   * set is routed to DELETE. Returns the saved mapping, or null when it was cleared.
   */
  saveRoles(
    odooJobId: number,
    jobTitle: string | undefined,
    roleNames: string[],
  ): Observable<JobRoleMapping | null> {
    const unique = Array.from(new Set((roleNames || []).filter(Boolean)));
    if (unique.length === 0) {
      return this.deleteMapping(odooJobId).pipe(map(() => null));
    }
    const body: JobRoleMappingRequest = { jobTitle, roleNames: unique };
    return this.http
      .put<JobRoleMapping>(`${this.mappingsUrl}/${odooJobId}`, body)
      .pipe(
        catchError((error) => {
          console.error('Error saving job-role mapping:', error);
          return throwError(() => error);
        }),
      );
  }

  /** Remove all role mappings for a job position. */
  deleteMapping(odooJobId: number): Observable<void> {
    return this.http.delete<void>(`${this.mappingsUrl}/${odooJobId}`).pipe(
      catchError((error) => {
        console.error('Error deleting job-role mapping:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Fetch selectable Keycloak realm roles for the add-roles dialog,
   * filtering out Keycloak's technical/internal roles.
   */
  getKeycloakRoles(
    page = 0,
    size = 1000,
    search = '',
  ): Observable<{ data: KeycloakRoleResponse[]; totalRecords: number }> {
    return this.apiRoleManagementService
      .getRoles(page, size, search, 'body', false, {
        httpHeaderAccept: 'application/json' as unknown as '*/*',
      })
      .pipe(
        map((response: PageInfoKeycloakRoleResponse) => {
          const data = (response.pageContent || []).filter((role) =>
            this.isSelectableRole(role.name),
          );
          return {
            data,
            totalRecords: response.totalElements ?? data.length,
          };
        }),
        catchError((error) => {
          console.error('Error fetching Keycloak roles:', error);
          return of({ data: [], totalRecords: 0 });
        }),
      );
  }

  private isSelectableRole(name?: string): boolean {
    if (!name) {
      return false;
    }
    if (TECHNICAL_ROLE_NAMES.has(name)) {
      return false;
    }
    return !TECHNICAL_ROLE_PREFIXES.some((prefix) => name.startsWith(prefix));
  }
}
